'use client'

import {
   Badge,
   Box,
   Button,
   Card,
   Container,
   SimpleGrid,
   Stack,
   Text
} from '@blocks'
import {
   siteFeatureCardShadow,
   sitePrimaryDeepHex,
   siteSectionBodyHex,
   siteSectionCardRadiusPx,
   siteSectionHeadingHex,
   siteTextOnDarkBandHex,
   siteTextOnDarkBandMutedHex,
   siteTextOnDarkBandSubtleHex,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitleDisplayFontSizeDesktop,
   siteTypographySectionTitleDisplayFontSizeMobile,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {IconExternalLink} from '@icons'
import Image from 'next/image'

/** Full card fill — green band to match Turtle Wax shelf branding. */
const turtleWaxCardBackgroundHex = '#00A651'

const turtleWaxLogoUrl =
   'https://www.turtlewaxnz.co.nz/cdn/shop/t/3/assets/logo.png?v=13048306051051086781694587935'

const chemriteCardBackgroundHex = '#ffffff'

const products = [
   {
      key: 'turtle-wax',
      logoUrl: turtleWaxLogoUrl,
      cardBackgroundHex: turtleWaxCardBackgroundHex,
      name: 'Turtle Wax',
      tagline: 'Exterior Protection & Finish',
      description:
         'A globally recognised brand trusted for over 75 years. We use Turtle Wax products to protect and enhance vehicle paintwork, delivering a quality finish after every wash.',
      websiteUrl: null as string | null,
      contentScheme: 'onGreen' as const
   },
   {
      key: 'chemrite',
      logoUrl:
         'https://www.chemrite.co.nz/cdn/shop/files/Chemrite_Logo_ba863e1b-d34d-427e-877c-2bcc53265477.jpg?v=1773669671&width=600',
      cardBackgroundHex: chemriteCardBackgroundHex,
      name: 'Chemrite',
      tagline: 'Professional Wash Chemicals',
      description:
         'Purpose-built cleaning solutions for professional wash environments. Chemrite products deliver consistent, effective results on all vehicle types and surfaces.',
      websiteUrl: 'https://www.chemrite.co.nz',
      contentScheme: 'onWhite' as const
   }
]

type Tone = 'light' | 'blue'

type Payload = Readonly<{
   /** Homepage placement hint (reserved for future band variants). */
   tone?: Tone
}>

/** Reserves space equivalent to the Chemrite CTA so both cards stay equal height. */
const productCardCallToActionMinHeightPx = 52

export const TrustedProducts = ({tone: _tone = 'light'}: Payload) => {
   const isMobile = useMediaQuery('(max-width: 768px)')
   const logoBandHeightPx = isMobile ? 200 : 240
   const contentPadding = isMobile ? 40 : 52
   /** Logo inset matches text block sides + bottom; extra top breathing room. */
   const productLogoInsetTopPx = contentPadding + (isMobile ? 16 : 20)

   return (
      <Box bg={'primary.1'}>
         <Container
            py={isMobile ? 88 : 140}
            size={'xl'}
         >
            <Stack gap={isMobile ? 52 : 72}>
               <Stack
                  align={'center'}
                  gap={16}
               >
                  <Badge
                     c={'primary.0'}
                     fw={900}
                     tt={'uppercase'}
                     size={isMobile ? 'lg' : 'xl'}
                     color={'primary.9'}
                     variant={'filled'}
                  >
                     Products we use
                  </Badge>
                  <Text
                     c={siteSectionHeadingHex}
                     fz={
                        isMobile
                           ? siteTypographySectionTitleDisplayFontSizeMobile
                           : siteTypographySectionTitleDisplayFontSizeDesktop
                     }
                     fw={800}
                     ta={'center'}
                     lh={1.1}
                  >
                     Trusted Brands, Every Wash
                  </Text>
                  <Text
                     c={siteSectionBodyHex}
                     fz={
                        isMobile
                           ? siteTypographySectionLeadFontSizeMobile
                           : siteTypographySectionLeadFontSizeDesktop
                     }
                     fw={500}
                     ta={'center'}
                     maw={560}
                     lh={1.75}
                  >
                     We only use professional-grade products chosen
                     for their quality, performance, and safe
                     application on all vehicle types.
                  </Text>
               </Stack>

               <SimpleGrid
                  cols={{base: 1, sm: 2}}
                  spacing={isMobile ? 22 : 32}
                  style={{alignItems: 'stretch'}}
               >
                  {products.map((product) => {
                     const isOnWhite =
                        product.contentScheme === 'onWhite'
                     const titleColor = isOnWhite
                        ? siteSectionHeadingHex
                        : siteTextOnDarkBandHex
                     const taglineColor = isOnWhite
                        ? siteSectionBodyHex
                        : siteTextOnDarkBandSubtleHex
                     const bodyColor = isOnWhite
                        ? siteSectionBodyHex
                        : siteTextOnDarkBandMutedHex

                     return (
                        <Card
                           key={product.key}
                           w={'100%'}
                           h={'100%'}
                           p={0}
                           style={{
                              overflow: 'hidden',
                              boxShadow: isOnWhite
                                 ? '0 8px 32px rgba(10, 55, 121, 0.1)'
                                 : siteFeatureCardShadow,
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor:
                                 product.cardBackgroundHex,
                              border: isOnWhite
                                 ? '1px solid #e2e8f4'
                                 : undefined
                           }}
                           radius={siteSectionCardRadiusPx}
                        >
                           <Card.Section
                              p={0}
                              style={{
                                 lineHeight: 0,
                                 flexShrink: 0,
                                 overflow: 'hidden',
                                 backgroundColor:
                                    product.cardBackgroundHex
                              }}
                           >
                              <Box
                                 pos={'relative'}
                                 w={'100%'}
                                 h={logoBandHeightPx}
                                 style={{
                                    backgroundColor:
                                       product.cardBackgroundHex,
                                    overflow: 'hidden'
                                 }}
                              >
                                 <Box
                                    pos={'absolute'}
                                    style={{
                                       top: productLogoInsetTopPx,
                                       left: contentPadding,
                                       right: contentPadding,
                                       bottom: contentPadding
                                    }}
                                 >
                                    <Box
                                       pos={'relative'}
                                       w={'100%'}
                                       h={'100%'}
                                    >
                                       <Image
                                          src={product.logoUrl}
                                          alt={product.name}
                                          fill
                                          sizes={
                                             isMobile
                                                ? '100vw'
                                                : '(max-width: 1200px) 50vw, 560px'
                                          }
                                          style={{
                                             objectFit: 'contain',
                                             objectPosition: 'center'
                                          }}
                                       />
                                    </Box>
                                 </Box>
                              </Box>
                           </Card.Section>
                           <Box
                              style={{
                                 flex: 1,
                                 display: 'flex',
                                 flexDirection: 'column',
                                 justifyContent: 'space-between',
                                 padding: contentPadding,
                                 paddingTop: isMobile ? 36 : 40,
                                 minHeight: 0,
                                 backgroundColor:
                                    product.cardBackgroundHex
                              }}
                           >
                              <Stack gap={isMobile ? 18 : 22}>
                                 <Text
                                    c={titleColor}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionTitleSecondaryFontSizeMobile
                                          : siteTypographySectionTitleSecondaryFontSizeDesktop
                                    }
                                    fw={800}
                                    lh={1.12}
                                 >
                                    {product.name}
                                 </Text>
                                 <Text
                                    c={taglineColor}
                                    fz={isMobile ? 16 : 17}
                                    fw={700}
                                    tt={'uppercase'}
                                    lts={1.2}
                                    lh={1.2}
                                 >
                                    {product.tagline}
                                 </Text>
                                 <Text
                                    c={bodyColor}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.75}
                                 >
                                    {product.description}
                                 </Text>
                              </Stack>
                              <Box
                                 style={{
                                    minHeight:
                                       productCardCallToActionMinHeightPx,
                                    marginTop: isMobile ? 16 : 20,
                                    display: 'flex',
                                    alignItems: 'flex-end'
                                 }}
                              >
                                 {product.websiteUrl ? (
                                    <Button
                                       component={'a'}
                                       href={product.websiteUrl}
                                       target={'_blank'}
                                       rel={'noopener noreferrer'}
                                       c={'white'}
                                       fw={700}
                                       size={'lg'}
                                       radius={'xl'}
                                       color={'primary'}
                                       variant={'gradient'}
                                       gradient={{
                                          from: sitePrimaryDeepHex,
                                          to: siteSectionHeadingHex,
                                          deg: 145
                                       }}
                                       rightSection={
                                          <IconExternalLink
                                             size={20}
                                             stroke={1.5}
                                          />
                                       }
                                    >
                                       Visit Chemrite
                                    </Button>
                                 ) : null}
                              </Box>
                           </Box>
                        </Card>
                     )
                  })}
               </SimpleGrid>
            </Stack>
         </Container>
      </Box>
   )
}
